import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiGet, apiSend } from '@/lib/api';
import { PageHeader, Card, Spinner, EmptyState, ErrorNote } from '@/components/ui';
import { formatCalendarDay, formatInstant } from '@/lib/dates';
import { askFlightTime } from '@/lib/flightPatch';
import { distanceLabel, type RouteFacts } from '@/lib/summary';

type Kind = 'mine' | 'other' | 'ignored';
interface Item extends RouteFacts {
  kind: Kind;
  id: string;
  date: string;
  departure_airport: string | null;
  arrival_airport: string | null;
  /** A logged leg's flight time, as entered (mine or the co-owner's). */
  flight_hours: number | null;
  /** An ignored detection's FlightAware airborne time — never added to flight time. */
  airborne_hours: number | null;
  flightaware_id?: string;
  flight_type?: string;
  note?: string | null;
  source?: string;
}

const KIND_META: Record<Kind, { label: string; badge: string }> = {
  mine: { label: 'Mine', badge: 'bg-sky-500/15 text-sky-300 ring-sky-500/30' },
  other: { label: 'Co-owner', badge: 'bg-slate-500/15 text-slate-300 ring-slate-500/30' },
  ignored: { label: 'Ignored', badge: 'bg-amber-500/15 text-amber-300 ring-amber-500/30' },
};
const FILTERS: Array<{ key: 'all' | Kind; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'mine', label: 'Mine' },
  { key: 'other', label: 'Co-owner' },
  { key: 'ignored', label: 'Ignored' },
];

export default function History() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | Kind>('all');

  function load() {
    apiGet<{ items: Item[] }>('/api/history')
      .then((d) => setItems(d.items || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  const shown = useMemo(() => (filter === 'all' ? items : items.filter((i) => i.kind === filter)), [items, filter]);
  // Flight time only. An ignored detection carries airborne time, a different
  // figure under a different name, and is never folded into this sum.
  const totalHours = useMemo(() => shown.reduce((s, i) => s + (i.flight_hours ?? 0), 0), [shown]);

  function logIt(i: Item) {
    navigate('/flights/new', {
      state: {
        prefill: {
          date: i.date,
          departure_airport: i.departure_airport,
          arrival_airport: i.arrival_airport,
          airborne_hours: i.airborne_hours ?? undefined,
          flightaware_id: i.flightaware_id,
        },
        detectionId: i.id,
      },
    });
  }
  async function toOther(i: Item) {
    const flight_hours = askFlightTime(i.airborne_hours);
    if (flight_hours === undefined) return;
    try { await apiSend(`/api/flightaware/detected/${i.id}/other`, 'POST', { flight_hours }); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed'); }
  }
  async function unignore(id: string) {
    try { await apiSend(`/api/flightaware/detected/${id}/reset`, 'POST'); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed'); }
  }
  // Undo a co-owner attribution that came from tracking: dropping the entry sends
  // the detection back to pending so it can be classified again.
  async function undoOther(id: string) {
    if (!confirm("Undo this co-owner attribution? The flight goes back to pending on Aircraft Tracking.")) return;
    try { await apiSend(`/api/other-flights/${id}`, 'DELETE'); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed'); }
  }

  // A co-owner row is a calendar day he typed into a date input; mine and the
  // ignored detections are moments that happened at a time of day. Formatting the
  // first as a local instant walks it back a day west of Greenwich — see
  // `lib/dates.ts`.
  const fmt = (i: Item) =>
    (i.kind === 'other' ? formatCalendarDay(i.date) : formatInstant(i.date)) ?? '—';

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6 lg:p-8">
      <PageHeader
        title="Aircraft History"
        subtitle="Every N313G flight — mine, the co-owner's, and ignored — in one timeline."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${
              filter === f.key ? 'bg-avio/20 text-white ring-1 ring-avio/40' : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-slate-200'
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-500">{shown.length} flights · {totalHours.toFixed(1)} h flight time</span>
      </div>

      {error && <div className="mb-4"><ErrorNote message={error} /></div>}

      {loading ? (
        <Spinner />
      ) : shown.length === 0 ? (
        <Card><EmptyState title="No flights yet" hint="Logged, co-owner, and ignored flights all appear here." /></Card>
      ) : (
        <Card className="overflow-hidden">
          <ul className="divide-y divide-white/5">
            {shown.map((i) => {
              const meta = KIND_META[i.kind];
              const inner = (
                <>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex w-20 justify-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${meta.badge}`}>{meta.label}</span>
                    <div>
                      <p className="ident text-white">{i.departure_airport || '—'} → {i.arrival_airport || '—'}</p>
                      <p className="text-xs text-slate-500">{fmt(i)}{i.note ? ` · ${i.note}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="hidden tabular-nums text-xs text-slate-500 sm:inline">{distanceLabel(i)}</span>
                    {i.kind === 'ignored' ? (
                      <span className="tabular-nums text-sm text-slate-500" title="FlightAware airborne time — not a logged flight time">
                        {i.airborne_hours != null && i.airborne_hours > 0 ? `${i.airborne_hours.toFixed(2)} h airborne` : '—'}
                      </span>
                    ) : (
                      <span className="tabular-nums text-sm text-slate-300" title={i.flight_hours == null ? 'No flight time entered yet' : 'Flight time'}>
                        {i.flight_hours != null ? `${i.flight_hours.toFixed(2)} h` : '—'}
                      </span>
                    )}
                    {i.kind === 'other' && i.source === 'adsb' && (
                      <div className="hidden gap-1 sm:flex">
                        <button onClick={() => undoOther(i.id)} className="rounded-md bg-white/5 px-2 py-1 text-xs text-slate-500 hover:bg-white/10" title="Not the co-owner's — move back to pending">Undo</button>
                      </div>
                    )}
                    {i.kind === 'ignored' && (
                      <div className="hidden gap-1 sm:flex">
                        <button onClick={() => logIt(i)} className="rounded-md bg-white/5 px-2 py-1 text-xs text-slate-300 hover:bg-avio/15 hover:text-white" title="This was my flight — log it">Log it</button>
                        <button onClick={() => toOther(i)} className="rounded-md bg-white/5 px-2 py-1 text-xs text-slate-300 hover:bg-white/10" title="Attribute to the co-owner">Co-owner</button>
                        <button onClick={() => unignore(i.id)} className="rounded-md bg-white/5 px-2 py-1 text-xs text-slate-500 hover:bg-white/10" title="Move back to pending">Un-ignore</button>
                      </div>
                    )}
                  </div>
                </>
              );
              return (
                <li key={`${i.kind}-${i.id}`} className="px-4 py-3 transition hover:bg-white/[0.03]">
                  {i.kind === 'mine' ? (
                    <Link to={`/flights/${i.id}`} className="flex items-center justify-between">{inner}</Link>
                  ) : (
                    <div className="flex items-center justify-between">{inner}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}
